import { useState } from "react";
import { Sparkles, Loader2, Phone, MessageCircle, Mail, Users, Clock, ArrowRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSequenceWithStats, useMessageTemplates } from "@/hooks/useOutreach";
import { useLeads } from "@/hooks/useLeads";
import { useContacts } from "@/hooks/useContacts";
import { useCalls } from "@/hooks/useCalls";
import { SequenceCard } from "@/components/outreach/SequenceCard";
import { AddSequenceDialog } from "@/components/outreach/AddSequenceDialog";
import { AddTemplateDialog } from "@/components/outreach/AddTemplateDialog";
import { TemplateCard } from "@/components/outreach/TemplateCard";
import { AIEmailTemplateGenerator } from "@/components/ai/AIEmailTemplateGenerator";
import { ScheduleCallDialog } from "@/components/calls/ScheduleCallDialog";
import { SendWhatsAppDialog } from "@/components/outreach/SendWhatsAppDialog";
import { LogCallDialog } from "@/components/calls/LogCallDialog";
import { toast } from "sonner";
import { format } from "date-fns";

export default function Outreach() {
  const [activeTab, setActiveTab] = useState("overview");
  const { data: sequences, isLoading: sequencesLoading } = useSequenceWithStats();
  const { data: templates, isLoading: templatesLoading } = useMessageTemplates();
  const { data: leads } = useLeads();
  const { data: contacts } = useContacts();
  const { data: calls } = useCalls();

  const hotLeads = leads?.filter(l => (l.lead_score || 0) >= 70).slice(0, 5) || [];
  const scheduledCalls = calls?.filter(c => c.status === 'scheduled').slice(0, 5) || [];
  const activeSequences = sequences?.filter(s => s.status === 'active') || [];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Outreach Hub</h1>
          <p className="text-muted-foreground">
            Manage calls, messages, and automated sequences
          </p>
        </div>
        <div className="flex items-center gap-2">
          <LogCallDialog />
          <ScheduleCallDialog />
          <SendWhatsAppDialog />
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <QuickActionCard
          icon={Phone}
          title="Make a Call"
          description={`${scheduledCalls.length} scheduled`}
          color="text-blue-500"
          bgColor="bg-blue-500/10"
        />
        <QuickActionCard
          icon={MessageCircle}
          title="WhatsApp"
          description="Send messages"
          color="text-green-500"
          bgColor="bg-green-500/10"
        />
        <QuickActionCard
          icon={Mail}
          title="Email"
          description={`${templates?.filter(t => t.channel === 'email').length || 0} templates`}
          color="text-purple-500"
          bgColor="bg-purple-500/10"
        />
        <QuickActionCard
          icon={Zap}
          title="Sequences"
          description={`${activeSequences.length} active`}
          color="text-amber-500"
          bgColor="bg-amber-500/10"
        />
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4" data-wizard="sequences">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="calls">Calls</TabsTrigger>
          <TabsTrigger value="messaging">Messaging</TabsTrigger>
          <TabsTrigger value="sequences">Sequences</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Hot Leads to Contact */}
            <Card className="glass">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  Hot Leads to Contact
                </CardTitle>
                <CardDescription>High-priority leads ready for outreach</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {hotLeads.length > 0 ? (
                  hotLeads.map((lead) => (
                    <div key={lead.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {lead.first_name} {lead.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {lead.company_name || lead.email}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {lead.lead_score || 0}%
                        </Badge>
                        <div className="flex gap-1">
                          {lead.phone && (
                            <ScheduleCallDialog
                              preSelectedLeadId={lead.id}
                              trigger={
                                <Button variant="ghost" size="icon" className="h-7 w-7">
                                  <Phone className="h-3.5 w-3.5" />
                                </Button>
                              }
                            />
                          )}
                          {lead.phone && (
                            <SendWhatsAppDialog
                              leadId={lead.id}
                              leadPhone={lead.phone}
                              leadName={`${lead.first_name} ${lead.last_name}`}
                              trigger={
                                <Button variant="ghost" size="icon" className="h-7 w-7">
                                  <MessageCircle className="h-3.5 w-3.5 text-green-500" />
                                </Button>
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No hot leads found
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Calls */}
            <Card className="glass">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  Upcoming Calls
                </CardTitle>
                <CardDescription>Your scheduled calls today</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {scheduledCalls.length > 0 ? (
                  scheduledCalls.map((call) => (
                    <div key={call.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {call.lead_id ? 'Lead Call' : call.contact_id ? 'Contact Call' : 'Scheduled Call'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {call.scheduled_at ? format(new Date(call.scheduled_at), 'MMM d, h:mm a') : 'No time set'}
                        </p>
                      </div>
                      <Button variant="outline" size="sm" className="gap-1">
                        <Phone className="h-3 w-3" />
                        Start
                      </Button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground mb-2">No calls scheduled</p>
                    <ScheduleCallDialog trigger={
                      <Button variant="outline" size="sm">Schedule Now</Button>
                    } />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* AI Recommendations */}
            <Card className="glass gradient-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  AI Insights
                </CardTitle>
                <CardDescription>Smart outreach recommendations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
                  <p className="text-sm">
                    {hotLeads.length > 0 
                      ? `Contact ${hotLeads[0]?.first_name} first - highest conversion probability at ${hotLeads[0]?.lead_score}%`
                      : "Create sequences to automate your outreach workflow"
                    }
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">
                    Best time to call: 10 AM - 12 PM based on your success rate
                  </p>
                </div>
                <Button variant="outline" size="sm" className="w-full" onClick={() => toast.success("AI recommendations applied")}>
                  Apply Suggestions
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Active Sequences */}
          {activeSequences.length > 0 && (
            <Card className="glass">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Active Sequences</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab("sequences")}>
                    View All <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {activeSequences.slice(0, 2).map((sequence) => (
                    <SequenceCard key={sequence.id} sequence={sequence} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Calls Tab */}
        <TabsContent value="calls" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Call Management</h2>
            <div className="flex gap-2">
              <LogCallDialog />
              <ScheduleCallDialog />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Scheduled Calls */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-base">Scheduled Calls</CardTitle>
                <CardDescription>Upcoming calls to make</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {scheduledCalls.length > 0 ? (
                  scheduledCalls.map((call) => (
                    <div key={call.id} className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium text-sm">
                          {call.phone_number || 'No number'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {call.scheduled_at ? format(new Date(call.scheduled_at), 'PPp') : 'No time'}
                        </p>
                        {call.notes && (
                          <p className="text-xs text-muted-foreground mt-1 truncate max-w-[200px]">
                            {call.notes}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="default" size="sm" className="gap-1">
                          <Phone className="h-3 w-3" />
                          Call Now
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Phone className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No scheduled calls</p>
                    <ScheduleCallDialog trigger={
                      <Button variant="outline" size="sm" className="mt-2">Schedule a Call</Button>
                    } />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Dial */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-base">Quick Dial</CardTitle>
                <CardDescription>Call leads or contacts directly</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {leads?.slice(0, 5).map((lead) => (
                  <div key={lead.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                    <div>
                      <p className="font-medium text-sm">{lead.first_name} {lead.last_name}</p>
                      <p className="text-xs text-muted-foreground">{lead.phone || 'No phone'}</p>
                    </div>
                    {lead.phone && (
                      <ScheduleCallDialog
                        preSelectedLeadId={lead.id}
                        trigger={
                          <Button variant="ghost" size="sm">
                            <Phone className="h-4 w-4" />
                          </Button>
                        }
                      />
                    )}
                  </div>
                ))}
                {(!leads || leads.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">No leads available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Messaging Tab */}
        <TabsContent value="messaging" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Messaging Center</h2>
            <div className="flex gap-2">
              <SendWhatsAppDialog />
              <AIEmailTemplateGenerator />
              <AddTemplateDialog />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Quick Message */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-green-500" />
                  Send WhatsApp
                </CardTitle>
                <CardDescription>Message leads and contacts instantly</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {leads?.filter(l => l.phone).slice(0, 5).map((lead) => (
                  <div key={lead.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                    <div>
                      <p className="font-medium text-sm">{lead.first_name} {lead.last_name}</p>
                      <p className="text-xs text-muted-foreground">{lead.phone}</p>
                    </div>
                    <SendWhatsAppDialog
                      leadId={lead.id}
                      leadPhone={lead.phone || ''}
                      leadName={`${lead.first_name} ${lead.last_name}`}
                      trigger={
                        <Button variant="outline" size="sm" className="gap-1">
                          <MessageCircle className="h-3 w-3" />
                          Message
                        </Button>
                      }
                    />
                  </div>
                ))}
                {(!leads || leads.filter(l => l.phone).length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">No contacts with phone numbers</p>
                )}
              </CardContent>
            </Card>

            {/* Templates */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-base">Message Templates</CardTitle>
                <CardDescription>Reusable templates for quick messaging</CardDescription>
              </CardHeader>
              <CardContent>
                {templatesLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : templates && templates.length > 0 ? (
                  <div className="space-y-2">
                    {templates.slice(0, 4).map((template) => (
                      <div key={template.id} className="p-2 rounded-lg border hover:bg-muted/50 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {template.channel}
                          </Badge>
                          <span className="font-medium text-sm">{template.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No templates yet</p>
                    <AddTemplateDialog />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sequences Tab */}
        <TabsContent value="sequences" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Outreach Sequences</h2>
            <AddSequenceDialog />
          </div>

          {/* AI Insights */}
          <Card className="glass gradient-border">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">AI Recommendations</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {sequences && sequences.length > 0 
                      ? `You have ${activeSequences.length} active sequences. Consider A/B testing subject lines to improve open rates.`
                      : "Create your first sequence to start automating your outreach."
                    }
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => toast.success("AI suggestion applied")}>
                  Apply
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Sequences Grid */}
          {sequencesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : sequences && sequences.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {sequences.map((sequence) => (
                <SequenceCard key={sequence.id} sequence={sequence} />
              ))}
            </div>
          ) : (
            <Card className="glass">
              <CardContent className="p-8 text-center">
                <Zap className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-muted-foreground mb-4">
                  No sequences yet. Create your first sequence to start automating outreach.
                </p>
                <AddSequenceDialog />
              </CardContent>
            </Card>
          )}

          {/* Templates Section */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Message Templates</CardTitle>
                <div className="flex items-center gap-2">
                  <AIEmailTemplateGenerator />
                  <AddTemplateDialog />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {templatesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : templates && templates.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {templates.map((template) => (
                    <TemplateCard key={template.id} template={template} />
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  <p className="text-sm mb-4">No templates yet. Create reusable message templates.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Quick Action Card Component
function QuickActionCard({ 
  icon: Icon, 
  title, 
  description, 
  color, 
  bgColor 
}: { 
  icon: React.ElementType; 
  title: string; 
  description: string; 
  color: string; 
  bgColor: string;
}) {
  return (
    <Card className="glass hover:shadow-md transition-shadow cursor-pointer">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${bgColor}`}>
            <Icon className={`h-5 w-5 ${color}`} />
          </div>
          <div>
            <p className="font-medium text-sm">{title}</p>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
